import React from "react";
import { Book, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ThemaCode, ThemaCodeType } from "reedy-data/api";
import { StyledTabItem, StyledTabs } from "../StyledTabs";
import { TabsContent } from "../ui/tabs";
import { Tab } from "../../shared/types/thema";
import { ThemaCodesTable } from "./ThemaCodesTable";
import { ThemaCodesResultsIndicator } from "./ThemaCodesResultsIndicator";
import { useThemaCodes } from "./hooks/useThemaCodes";

export const ThemaCodesTabs = ({
  activeTab,
  onTabChange,
  query,
  themaLanguage,
  selected = [],
  onToggleSelect,
  showNotesInline,
  onDrillDown,
  renderRowAction,
}: {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  query: string;
  themaLanguage: "en" | "de";
  selected?: ThemaCode[];
  onToggleSelect?: (code: ThemaCode) => void;
  showNotesInline?: boolean;
  onDrillDown: (code: ThemaCode, navigationStack: ThemaCode[]) => void;
  renderRowAction?: (item: ThemaCode) => React.ReactNode;
}) => {
  const { t } = useTranslation(["components"]);

  const {
    data,
    isLoading,
    error,
    navigationStack,
    handleDrillDown,
    handleNavigateToLevel,
    scrollContainerRef,
    searchQuery,
    count,
    allSearchResults,
    hasMorePages,
    isLoadingMore,
  } = useThemaCodes({
    query,
    activeTab,
    themaLanguage,
    pageSize: 50,
  });

  const tabs: StyledTabItem<Tab>[] = [
    {
      value: Tab.SUBJECTS,
      label: t("components:themaCodes.subjects"),
      icon: <Book className="w-4 h-4" />,
      count: count[ThemaCodeType.SUBJECT],
    },
    {
      value: Tab.QUALIFIERS,
      label: t("components:themaCodes.qualifiers"),
      icon: <MapPin className="w-4 h-4" />,
      count: count[ThemaCodeType.QUALIFIER],
    },
  ];

  return (
    <StyledTabs
      activeTab={activeTab}
      onTabChange={onTabChange}
      tabs={tabs}
      variant="pill"
      className="flex flex-col flex-1 min-h-0"
    >
      {[Tab.SUBJECTS, Tab.QUALIFIERS].map((tab) => (
        <TabsContent
          value={tab}
          className="mt-2 flex-1 flex flex-col min-h-0"
          key={tab}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <ThemaCodesTable
              data={data}
              isLoading={isLoading}
              error={error}
              query={query}
              selected={selected}
              onToggle={onToggleSelect}
              onDrillDown={(code) => {
                onDrillDown(code, navigationStack);
                handleDrillDown(code);
              }}
              onNavigateToLevel={handleNavigateToLevel}
              navigationStack={navigationStack}
              type={tab === Tab.SUBJECTS ? "subjects" : "qualifiers"}
              showNotesInline={showNotesInline}
              scrollRef={scrollContainerRef}
              renderRowAction={renderRowAction}
            />
            <ThemaCodesResultsIndicator
              query={query}
              totalResults={searchQuery.data?.total || 0}
              currentResults={allSearchResults.length}
              isLoadingMore={isLoadingMore}
              hasMorePages={hasMorePages}
              isSearching={searchQuery.isLoading}
            />
          </div>
        </TabsContent>
      ))}
    </StyledTabs>
  );
};
